import SettingsChipsField from '@/components/settings/SettingsChipsField.tsx';
import { SettingsTextField } from '@/components/settings/SettingsTextField.tsx';
import { SettingTitle } from '@/components/settings/SettingsTitle.tsx';
import { AuthContext } from '@/contexts/AuthContext.tsx';
import { DashboardContext } from '@/contexts/DashboardContext.tsx';
import { SnackbarContext } from '@/contexts/SnackbarContext.tsx';
import { UserContext } from '@/contexts/UserContext.tsx';
import RefetchAccountDataModal from '@/modals/RefetchAccountDataModal.tsx';
import { scrapeAccountData } from '@/services/AccountsService.ts';
import { BrandVoiceConfig } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import Label from '@superfunnel/ui-core/v2/Label';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { useContext, useEffect, useState } from 'react';

export const BrandVoiceSettings = () => {
  const { accountAiSettings, updateAccountAiSettings } =
    useContext(DashboardContext);
  const { selectedAccount } = useContext(UserContext);
  const { firebaseUser, isSupportAdmin } = useContext(AuthContext);
  const { show } = useContext(SnackbarContext);
  const [edit, setEdit] = useState<boolean>(false);
  const [showRefetchModal, setShowRefetchModal] = useState<boolean>(false);
  const [brandVoice, setBrandVoice] = useState<Partial<BrandVoiceConfig>>({
    brandSummary: '',
    companyDescription: '',
    tone: ['Professional', 'Confident', 'Friendly'],
  });

  // Initialize from context
  useEffect(() => {
    if (accountAiSettings?.brandVoice) {
      setBrandVoice(accountAiSettings.brandVoice);
    }
  }, [accountAiSettings?.brandVoice]);

  // Check for changes to show/hide save button
  useEffect(() => {
    if (!accountAiSettings?.brandVoice) {
      setEdit(
        brandVoice.brandSummary !== '' ||
          brandVoice.companyDescription !== '' ||
          (brandVoice.tone?.length ?? 0) > 0,
      );
      return;
    }

    const current = accountAiSettings.brandVoice;
    setEdit(
      brandVoice.brandSummary !== current.brandSummary ||
        brandVoice.companyDescription !== current.companyDescription ||
        JSON.stringify(brandVoice.tone?.sort()) !==
          JSON.stringify(current.tone?.sort()),
    );
  }, [brandVoice, accountAiSettings?.brandVoice]);

  const onSave = () => {
    setEdit(false);
    updateAccountAiSettings({ brandVoice });
  };

  const onDiscard = () => {
    setEdit(false);
    if (accountAiSettings?.brandVoice) {
      setBrandVoice(accountAiSettings.brandVoice);
    } else {
      setBrandVoice({
        brandSummary: '',
        companyDescription: '',
        tone: ['Professional', 'Confident', 'Friendly'],
      });
    }
  };

  const handleRefetchConfirm = async (url: string) => {
    if (!selectedAccount || !firebaseUser) return;

    try {
      await scrapeAccountData(selectedAccount.id, url, firebaseUser.uid, [
        'aiSettings',
      ]);
      show('AI settings refresh started. This may take a minute.', 'success');
    } catch (error) {
      console.error('Error refetching AI settings:', error);
      show('Failed to refresh AI settings', 'warn');
      throw error;
    }
  };

  const toneOptions = [
    'Friendly',
    'Professional',
    'Playful',
    'Formal',
    'Persuasive',
    'Inspirational',
    'Technical',
    'Confident',
  ];

  return (
    <div
      className={
        'relative grid h-full w-full max-w-full grid-rows-[1fr_auto] overflow-hidden'
      }
    >
      <div className={'h-full overflow-y-auto'}>
        <div className={'mx-auto flex max-w-161 flex-col gap-5 px-2.5'}>
          <div className="flex items-start justify-between">
            <SettingTitle
              title="Brand & Voice"
              description="Define your brand identity and tone of voice for AI-generated content."
            />
            {isSupportAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRefetchModal(true)}
              >
                Refetch AI Settings
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-5">
            <SettingsTextField
              title="Brand summary"
              helpText="A concise description of your brand, audience, and value proposition. Example: [Brand] helps [Audience] achieve [Outcome] with [Product]."
              value={brandVoice.brandSummary || ''}
              setValue={(v: string) =>
                setBrandVoice({ ...brandVoice, brandSummary: v })
              }
              placeholder="[Brand] helps [Audience] achieve [Outcome] with [Product]."
            />

            <div className="space-y-2">
              <Label className="text-base-700 text-sm font-medium">
                Company description
              </Label>
              <p className="mb-2 text-sm text-gray-600">
                Detailed description of what your company does, its mission, and
                key offerings
              </p>
              <TextArea
                value={brandVoice.companyDescription || ''}
                onChange={(e) =>
                  setBrandVoice({
                    ...brandVoice,
                    companyDescription: e.target.value,
                  })
                }
                placeholder="Provide a comprehensive description of your company, including what you do, your mission, target market, and key products or services..."
                rows={6}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-base-700 text-sm font-medium">
                Tone of voice
              </Label>
              <p className="mb-2 text-sm text-gray-600">
                Choose multiple tone characteristics that define your brand
                voice
              </p>
              <SettingsChipsField
                selected={
                  brandVoice.tone?.map((t) => ({ id: t, title: t })) || []
                }
                setSelected={(selected) =>
                  setBrandVoice({
                    ...brandVoice,
                    tone: selected.map((s) => s.title),
                  })
                }
                options={toneOptions.map((opt) => ({ id: opt, title: opt }))}
              />
            </div>
          </div>
        </div>
      </div>
      {edit && (
        <div
          className={
            'mx-auto flex w-full max-w-161 justify-end gap-3 bg-white px-2.5 pt-2'
          }
        >
          <Button variant={'outline'} onClick={onDiscard}>
            Discard
          </Button>
          <Button onClick={onSave}>Save</Button>
        </div>
      )}
      <RefetchAccountDataModal
        open={showRefetchModal}
        onClose={() => setShowRefetchModal(false)}
        onConfirm={handleRefetchConfirm}
        title="Refetch AI Settings"
        description="Enter the website URL to scrape AI settings from."
        defaultUrl={selectedAccount?.data()?.websiteUrl || ''}
      />
    </div>
  );
};
